import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import { ValidationBadge } from './ValidationBadge';
import { coordLabel, monsterPath, spotPath } from '../../utils/worldFormat';
import type { QuestRoute } from '../../utils/progressionLookup';
const SHOW = 6;

/**
 * Drops de misión documentados. Dependen de una misión activa y de sus condiciones:
 * no son drops generales disponibles para cualquier jugador.
 */
export function QuestDropSummary({
  quest
}: {
  quest: QuestRoute[];
}) {
  const targets = quest.flatMap(q => q.targets);
  const unique = Array.from(new Map(targets.map(t => [`${t.monsterId}|${t.mapId ?? ''}`, t])).values());
  const mapsCount = new Set(unique.map(t => t.mapId).filter(m => m !== undefined)).size;
  const conditions = Array.from(new Set(quest.map(q => q.route.conditions).filter(Boolean))) as string[];
  const variants = Array.from(new Set(quest.map(q => q.variantName).filter(Boolean))) as string[];
  return <div className="mt-2 rounded-xl border border-emerald-300/20 bg-emerald-300/[0.04] p-3" data-id="element-1018" data-mp="1u3odbn0vxzcs">
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white" data-id="element-1019" data-mp="1pd7vzrty1tl5">
        <ScrollText className="h-4 w-4 text-emerald-200" aria-hidden="true" data-id="element-1020" data-mp="1ke4s4app9ter" />
        Se obtiene durante la misión
        <ValidationBadge status="documentado" label="Ruta de misión documentada" data-id="element-1021" data-mp="1ffbqbfjrzquf" />
      </p>
      <p className="mt-1 text-xs text-amber-100" data-id="element-1022" data-mp="2d2slktklrp3e">Requiere tener la misión activa{conditions.length ? `: ${conditions.join(' · ')}` : '.'} No es un drop general.</p>
      {variants.length > 0 && <p className="mt-1 text-xs text-gray-400" data-id="element-1023" data-mp="281wd02pb3ern">Variante: {variants.join(', ')}</p>}
      {unique.length > 0 ? <>
          <p className="mt-2 text-xs text-gray-400" data-id="element-1024" data-mp="1z3khpndhvssv">
            {unique.length} {unique.length === 1 ? 'monstruo' : 'combinaciones de monstruo y mapa'}
            {mapsCount ? ` en ${mapsCount} ${mapsCount === 1 ? 'mapa' : 'mapas'}` : ''}. Posibles, no garantizados.
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1025" data-mp="p5axtt1hcnds">
            {unique.slice(0, SHOW).map(t => {
          const spot = t.spawns.length === 1 ? t.spawns[0] : undefined;
          return <li key={`${t.monsterId}-${t.mapId ?? ''}`} data-id="element-1026" data-mp="f7db59z9qyna">
                  <Link to={spot ? spotPath(spot.id) : monsterPath(t.monsterId)} className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-1027" data-mp="f8y79418epn9">
                    <span className="font-semibold" data-id="element-1028" data-mp="3sm9x3adhsicu">{t.monsterName}</span>
                    {t.mapName && <span className="text-gray-400" data-id="element-1029" data-mp="1f772m9e4i47">· {t.mapName}</span>}
                    {spot && coordLabel(spot) && <span className="font-mono text-[11px] text-gray-500" data-id="element-1030" data-mp="3jlbumwipciwd">· {coordLabel(spot)}</span>}
                  </Link>
                </li>;
        })}
          </ul>
          {unique.length > SHOW && <p className="mt-1 text-xs text-gray-500" data-id="element-1031" data-mp="34xl6fneihkps">+{unique.length - SHOW} más en “Dónde conseguirlo”.</p>}
        </> : <p className="mt-1 text-xs text-gray-400" data-id="element-1032" data-mp="300urx4icux6h">Monstruos de la ruta de misión pendientes de documentar.</p>}
    </div>;
}